import {gql} from "@apollo/client";
import {ApolloClientQuery} from "./_setup";
import { createLogSession } from "../restful/logs/logs";

const userId = localStorage.getItem("userid")

const GET_E_WALLET_SUMMARY_BALANCE = gql`
    query getEWalletSummaryBalance($filter: EWalletSummaryBalanceInput, $limit: Int, $offset: Int, $orderBy: String) {
        getEWalletSummaryBalance(filter: $filter, limit: $limit, offset: $offset, orderBy: $orderBy) {
            ... on EWalletSummaryBalanceArray {
                data {
                    currencyCode
                    totalBalance
                    totalEWallets
                }
            }
            ... on BaseError {
                errKey
                errMsg
            }
        }
    }
`;

export async function getEWalletSummaryBalance({filter, limit, offset, orderBy}) {
    const variables = {
        filter, limit, offset, orderBy,
        context: {
            headers: {
                authorization: "Bearer " + localStorage.getItem("a_t"),
            },
        },
    };
    return await ApolloClientQuery(GET_E_WALLET_SUMMARY_BALANCE, variables, false);
}

const GET_E_WALLET = gql`
    query getEWallet($filter: EWalletSearchInput, $limit: Int, $offset: Int, $orderBy: String) {
        getEWallet(filter: $filter, limit: $limit, offset: $offset, orderBy: $orderBy) {
            ... on EWalletArray {
                data {
                    _id
                    id
                    user {
                        fullName
                    }
                    balance
                    description
                    createdBy {
                        username
                    }
                    updatedBy {
                        username
                    }
                    createdAt
                    updatedAt
                }
            }
            ... on BaseError {
                errKey
                errMsg
            }
        }
    }
`;

export async function getEWallet(userid) {
    const variables = {
        filter: {
            userIdList: [userid],
        },
        context: {
            headers: {
                authorization: "Bearer " + localStorage.getItem("a_t"),
            },
        },
    };
    return await ApolloClientQuery(GET_E_WALLET, variables, false);
}

const GET_CLIENT_E_WALLETS = gql`
    query getClientEWallets($filter: GetClientEWalletsSearchInput, $limit: Int, $offset: Int, $orderBy: String) {
        getClientEWallets(filter: $filter, limit: $limit, offset: $offset, orderBy: $orderBy) {
            ... on EWalletArray {
                data {
                    _id
                    id
                    balance
                    currencyCode
                    isFreezed
                    isDepositEnable
                    isWithdrawalEnable
                    isExchangeEnable
                    isDeleted
                    createdAt
                }
                total
            }
            ... on BaseError {
                errObj
                errKey
                errMsg
            }
        }
    }
`;

export async function getClientEWallets({filter, limit, offset, orderBy}) {
    const variables = {
        filter: {
            user: userId,
            ...filter,
        },
        limit,
        offset,
        orderBy,
        context: {
            headers: {
                authorization: "Bearer " + localStorage.getItem("a_t"),
            },
        },
    };
    return await ApolloClientQuery(GET_CLIENT_E_WALLETS, variables, false);
}

const GET_E_WALLET_BY_ID = gql`
    query getEWalletByID($id: String!) {
        getEWalletByID(id: $id) {
            ... on EWallet {
                _id
                id
                user {
                    fullName
                }
                currencyCode
                balance
                createdBy {
                    username
                }
                updatedBy {
                    username
                }
                createdAt
                updatedAt
            }
            ... on BaseError {
                errKey
                errMsg
            }
        }
    }
`;

export async function getEWalletById(id) {
    const variables = {
        id: id,
        context: {
            headers: {
                authorization: "Bearer " + localStorage.getItem("a_t"),
            },
        },
    };
    return await ApolloClientQuery(GET_E_WALLET_BY_ID, variables, false);
}

const GET_E_WALLET_CURRENCY_SETTING = gql`
    query getEWalletCurrencySetting($filter: SearchEWalletCurrencySettingInput) {
        getEWalletCurrencySetting(filter: $filter) {
            ... on EWalletCurrencySettingArray {
                data {
                    _id
                    displayName
                    currencyCode
                    digit
                    rateType
                    rate
                    buyRateWithMarkup
                    sellRateWithMarkup
                    buyMarkupPercentage
                    sellMarkupPercentage
                    isBaseCurrency
                    isEnabled
                    isDeleted
                    createdAt
                    updatedAt
                }
                total
            }
            ... on BaseError {
                errKey
                errMsg
                errObj
            }
        }
    }
`;

export async function getEWalletCurrencySetting(filter) {
    const variables = {
        filter,
        context: {
            headers: {
                authorization: "Bearer " + localStorage.getItem("a_t"),
            },
        },
    };
    return await ApolloClientQuery(GET_E_WALLET_CURRENCY_SETTING, variables, false);
}

const CREATE_E_WALLET = gql`
    mutation createEWallet($input: CreateEWalletInput) {
        createEWallet(input: $input) {
            ... on EWallet {
                id
            }
            ... on BaseError {
                errKey
                errMsg
                errObj
            }
            ... on CrmTaskApproval {
                errMsg
                msg
            }
        }
    }
`;

export async function createEWallet(input) {
    const session = createLogSession("Ewallet", "Ewallet", "createEwallet");
    session.create({
        description: "Creating Ewallet",
        logData: { input },
    });
    const variables = {
        input,
        context: {
            headers: {
                authorization: "Bearer " + localStorage.getItem("a_t"),
            },
        },
    };
    try {
        const res = await ApolloClientQuery(CREATE_E_WALLET, variables, true);
        session.create({
            description: "Ewallet created",
            status: "info",
            logData: { input },
        });
        return res;
    } catch (e) {
        session.create({
            description: "Failed to create Ewallet",
            status: "error",
            logData: { input, error: e?.message },
        });
        throw e;
    }
}

const CLIENT_E_WALLET_EXCHANGE = gql`
    mutation clientEWalletExchange($input: CreateEWalletExchangeInput!) {
        clientEWalletExchange(input: $input) {
            ... on EWalletExchange {
                _id
                fromCurrencyCode
                toCurrencyCode
                rate
                rateWithMarkup
                markupPercentage
                fromAmount
                toAmount
                actualAmount
                description
                transactionStatus
                createdAt
                updatedAt
                markupAmount
                transactionNo
                transactionDate
                remark
                comment
                isProcessing
                isDeleted
            }
            ... on BaseError {
                errKey
                errMsg
                errObj
            }
        }
    }
`;

export async function clientEWalletExchange(input) {
    const session = createLogSession("Ewallet", "Exchange", "clientEWalletExchange");
    session.create({
        description: "Exchanging Ewallet currency",
        logData: { input },
    });
    const variables = {
        input,
        context: {
            headers: {
                authorization: "Bearer " + localStorage.getItem("a_t"),
            },
        },
    };
    try {
        const res = await ApolloClientQuery(CLIENT_E_WALLET_EXCHANGE, variables, true);
        session.create({
            description: "Ewallet currency exchanged",
            status: "info",
            logData: { input },
        });
        return res;
    } catch (e) {
        session.create({
            description: "Ewallet currency exchange failed",
            status: "error",
            logData: { input, error: e?.message },
        });
        throw e;
    }
}

const GET_E_WALLET_STATEMENT = gql`
    query getEWalletStatement($filter: EWalletStatementSearchInput, $limit: Int, $offset: Int, $orderBy: String) {
        getEWalletStatement(filter: $filter, limit: $limit, offset: $offset, orderBy: $orderBy) {
            ... on EWalletStatementArray {
                data {
                    _id
                    statementDate
                    type
                    description
                    user {
                        _id
                    }
                    debit
                    credit
                    balance
                    refNo
                    eWalletTransactionId
                    transactionType
                    importId
                }
            }
            ... on BaseError {
                errKey
                errMsg
            }
        }
    }
`;

export async function getEWalletStatement(user, limit, offset) {
    const variables = {
        filter: {
            user: user,
        },
        limit: limit || null,
        offset: offset || null,
        context: {
            headers: {
                authorization: "Bearer " + localStorage.getItem("a_t"),
            },
        },
    };
    return await ApolloClientQuery(
        GET_E_WALLET_STATEMENT,
        variables,
        false
    );
}

const GET_E_WALLET_STATEMENT_COUNT = gql`
    query getEWalletStatementCount($filter: EWalletStatementSearchInput) {
        getEWalletStatementCount(filter: $filter)
    }
`;

export async function getEWalletStatementCount(user) {
    const variables = {
        filter: {
            user: user,
            isDeleted: false,
        },
        context: {
            headers: {
                authorization: "Bearer " + localStorage.getItem("a_t"),
            },
        },
    };
    return await ApolloClientQuery(
        GET_E_WALLET_STATEMENT_COUNT,
        variables,
        false
    );
}

const GET_CLIENT_E_WALLET_RECORD = gql`
    query getClientEWalletRecord($filter: EWalletClientSearchInput, $limit: Int, $offset: Int, $orderBy: String) {
        getClientEWalletRecord(filter: $filter, limit: $limit, offset: $offset, orderBy: $orderBy) {
            ... on EWalletRecordArray {
                data {
                    transactionDate 
                    transactionNo 
                    eWalletTransactionId 
                    description 
                    remark 
                    transactionType 
                    amount
                    transactionStatus
                    comment
                    fromCurrencyCode
                    currencyCode
                    toCurrencyCode
                }
                count
            }
            ... on BaseError {
                errKey
                errMsg
            }
        }
    }
`;

export async function getClientEWalletRecord(filter, limit, offset, orderBy) {
    const variables = {
        filter: filter,
        limit: limit,
        offset: offset,
        orderBy: orderBy,
        context: {
            headers: {
                authorization: "Bearer " + localStorage.getItem("a_t"),
            },
        },
    };
    return await ApolloClientQuery(
        GET_CLIENT_E_WALLET_RECORD,
        variables,
        false
    );
}

const GET_E_WALLET_DEPOSIT = gql`
    query getEWalletDeposit($filter: EWalletDepositSearchInput, $limit: Int, $offset: Int, $orderBy: String) {
        getEWalletDeposit(filter: $filter, limit: $limit, offset: $offset, orderBy: $orderBy) {
            ... on EWalletDepositArray {
                data {
                    transactionNo
                    transactionDate
                    description
                    amount
                    transactionStatus
                    psp
                }
            }
            ... on BaseError {
                errKey
                errMsg
            }
        }
    }
`;

export async function getEWalletDeposit(user, limit, offset, orderBy) {
    const variables = {
        filter: {
            user: user,
            transactionStatus: ["rejected", "approved", "pending"],
        },
        limit: limit || null,
        offset: offset || null,
        orderBy: orderBy,
        context: {
            headers: {
                authorization: "Bearer " + localStorage.getItem("a_t"),
            },
        },
    };
    return await ApolloClientQuery(
        GET_E_WALLET_DEPOSIT,
        variables,
        false
    );
}

const GET_E_WALLET_WITHDRAWAL = gql`
    query getEWalletWithdrawal($filter: EWalletWithdrawalSearchInput, $limit: Int, $offset: Int, $orderBy: String) {
        getEWalletWithdrawal(filter: $filter, limit: $limit, offset: $offset, orderBy: $orderBy) {
            ... on EWalletWithdrawalArray {
                data {
                    _id
                    transactionNo
                    transactionDate
                    amount
                    transactionStatus
                    remark
                    comment
                    bankInformationId {
                        formData
                    }
                    psp
                    taskStatus
                }
            }
            ... on BaseError {
                errKey
                errMsg
            }
        }
    }
`;

export async function getEWalletWithdrawal(user, limit, offset, orderBy) {
    const variables = {
        filter: {
            user: user,
            transactionStatus: ["rejected", "approved", "pending"],
        },
        limit: limit || null,
        offset: offset || null,
        orderBy: orderBy,
        context: {
            headers: {
                authorization: "Bearer " + localStorage.getItem("a_t"),
            },
        },
    };
    return await ApolloClientQuery(
        GET_E_WALLET_WITHDRAWAL,
        variables,
        false
    );
}

const CLIENT_DEPOSIT = gql`
    mutation clientDeposit($id: String!, $input: EWalletClientDepositInput) {
        clientDeposit(id: $id, input: $input) {
            ... on EWalletClientDeposit {
                status
                url
            }
            ... on CrmTaskApproval {
                msg
            }
            ... on BaseError {
                errKey
                errMsg
                errObj
            }
        }
    }
`;

export async function clientDeposit(userid, input) {
    const session = createLogSession("Ewallet", "Deposit", "clientDeposit");
    session.create({
        description: `Submitting Ewallet deposit via ${input?.psp}`,
        logData: { userId: userid, input },
    });
    const variables = {
        id: userid,
        input: input,
        context: {
            headers: {
                authorization: "Bearer " + localStorage.getItem("a_t"),
            },
        },
    };
    try {
        const res = await ApolloClientQuery(CLIENT_DEPOSIT, variables, true);
        session.create({
            description: `Ewallet deposit submitted via ${input?.psp}`,
            status: "info",
            logData: { userId: userid, input },
        });
        return res;
    } catch (e) {
        session.create({
            description: `Ewallet deposit via ${input?.psp} failed`,
            status: "error",
            logData: { userId: userid, input, error: e?.message },
        });
        throw e;
    }
}

const CLIENT_WITHDRAWAL = gql`
    mutation clientWithdrawal($id: String!, $input: EWalletClientWithdrawalInput) {
        clientWithdrawal(id: $id, input: $input) {
            ... on EWalletClientWithdrawal {
                status
            }
            ... on CrmTaskApproval {
                msg
            }
            ... on BaseError {
                errKey
                errMsg
                errObj
            }
        }
    }
`;

export async function clientWithdrawal(userid, input) {
    const session = createLogSession("Ewallet", "Withdrawal", "clientWithdrawal");
    session.create({
        description: `Submitting Ewallet withdrawal via ${input?.psp}`,
        logData: { userId: userid, input },
    });
    const variables = {
        id: userid,
        input: input,
        context: {
            headers: {
                authorization: "Bearer " + localStorage.getItem("a_t"),
            },
        },
    };
    try {
        const res = await ApolloClientQuery(CLIENT_WITHDRAWAL, variables, true);
        session.create({
            description: `Ewallet withdrawal submitted via ${input?.psp}`,
            status: "info",
            logData: { userId: userid, input },
        });
        return res;
    } catch (e) {
        session.create({
            description: `Ewallet withdrawal via ${input?.psp} failed`,
            status: "error",
            logData: { userId: userid, input, error: e?.message },
        });
        throw e;
    }
}

const CANCEL_WITHDRAWAL = gql`
    mutation cancelEWalletWithdrawal($id: String!) {
        cancelEWalletWithdrawal(id: $id) {
            ... on EWalletWithdrawal {
                _id
                transactionNo
                transactionDate
                amount
                transactionStatus
            }
            ... on CrmTaskApproval {
                msg
            }
            ... on BaseError {
                errKey
                errMsg
            }
        }
    }
`;

export async function cancelEWalletWithdrawal(id) {
    const session = createLogSession("Ewallet", "Withdrawal", "cancelEWalletWithdrawal");
    session.create({
        description: "Cancelling Ewallet withdrawal",
        logData: { withdrawalId: id },
    });
    const variables = {
        id: id,
        context: {
            headers: {
                authorization: "Bearer " + localStorage.getItem("a_t"),
            },
        },
    };
    try {
        const res = await ApolloClientQuery(CANCEL_WITHDRAWAL, variables, true);
        session.create({
            description: "Ewallet withdrawal cancelled",
            status: "info",
            logData: { withdrawalId: id },
        });
        return res;
    } catch (e) {
        session.create({
            description: "Failed to cancel Ewallet withdrawal",
            status: "error",
            logData: { withdrawalId: id, error: e?.message },
        });
        throw e;
    }
}

const GET_E_WALLET_DEPOSIT_COUNT = gql`
    query getEWalletDepositCount($filter: EWalletDepositSearchInput) {
        getEWalletDepositCount(filter: $filter)
    }
`;

export async function getEWalletDepositCount(user) {
    const variables = {
        filter: {
            user: user,
            transactionStatus: ["rejected", "approved", "pending"],
        },
        context: {
            headers: {
                authorization: "Bearer " + localStorage.getItem("a_t"),
            },
        },
    };
    return await ApolloClientQuery(
        GET_E_WALLET_DEPOSIT_COUNT,
        variables,
        false
    );
}

const GET_E_WALLET_WITHDRAWAL_COUNT = gql`
    query getEWalletWithdrawalCount($filter: EWalletWithdrawalSearchInput) {
        getEWalletWithdrawalCount(filter: $filter)
    }
`;

export async function getEWalletWithdrawalCount(user) {
    const variables = {
        filter: {
            user: user,
            transactionStatus: ["rejected", "approved", "pending"],
        },
        context: {
            headers: {
                authorization: "Bearer " + localStorage.getItem("a_t"),
            },
        },
    };
    return await ApolloClientQuery(
        GET_E_WALLET_WITHDRAWAL_COUNT,
        variables,
        false
    );
}

const GET_BANK_INFO = gql`
    query getBankInformation($filter: BankInformationInput) {
        getBankInformation(filter: $filter) {
            ... on BankInformationArray {
                data {
                    user {
                        _id
                    }
                    psp {
                        _id
                    }
                    formData
                }
            }
        }
    }
`;

export async function getBankInformation(user, psp) {
    const variables = {
        filter: {
            user: user,
            psp: psp,
        },
        context: {
            headers: {
                authorization: "Bearer " + localStorage.getItem("a_t"),
            },
        },
    };
    return await ApolloClientQuery(GET_BANK_INFO, variables, false);
}
